import { toUnicode } from 'punycode/'; // Trailing forward slash required until deprecated core package is removed. https://github.com/bestiejs/punycode.js/issues/79#issuecomment-373773560
import set from 'lodash/set';
import { sort as sortUtil } from '@/utils';
import { defaultFilters } from './state';

const filterDefaults = defaultFilters();
const fixDomains = (sites) =>
	sites.map((site) => {
		site.domain = toUnicode(site.domain);
	});

export default {
	setSearch(state, payload) {
		state.search = payload;
	},
	setSearching(state, payload) {
		state.searching = payload;
	},
	setFolders(state, payload) {
		state.folders = payload || [];
	},
	addCampaigns(state, payload = []) {
		const campaigns = state.campaigns.length ? state.campaigns : [];

		payload.forEach((campaign) => {
			const sites = campaign.sites || [];
			fixDomains(sites);
			campaign.sites = sites;

			campaigns.push(campaign);
		});

		this._vm.$set(state, 'campaigns', campaigns);
	},
	updateCampaignMeta(state, { campaignId, meta }) {
		const index = state.campaigns.findIndex((c) => c.id === campaignId);
		if (-1 === index) {
			return;
		}

		const campaign = { ...state.campaigns[index] };

		set(campaign, 'wp.post_meta', meta);

		this._vm.$set(state.campaigns, index, campaign);
	},
	updateCampaignValue(state, { campaignId, key, value, valueType = 'string' }) {
		const index = state.campaigns.findIndex((c) => c.id === campaignId);
		if (-1 === index) {
			return;
		}

		const campaign = { ...state.campaigns[index] };

		switch (valueType) {
			case 'numericBool':
				value = value && '0' !== value ? '1' : '0';
				break;
			case 'positiveNumber':
				value = parseInt(value, 0);
				value = 0 < value ? value : 1;
				break;
			case 'bool':
				value = Boolean(value && '0' !== value);
				break;
			case 'array':
				value = value || [];
				value = Array.isArray(value) ? value : [value];
				break;
			case 'object':
				// Leave it be.
				break;
			// case 'string':
			default:
				value = value || 0 === value ? String(value) : '';
				break;
		}

		set(campaign, key, value);

		this._vm.$set(state.campaigns, index, campaign);
	},
	updateCampaign(state, { index, campaign }) {
		if (undefined === index) {
			index = state.campaigns.findIndex((c) => c.id === campaign.id);
		}

		this._vm.$set(state.campaigns, index, campaign);
	},
	setCampaigns(state, payload) {
		const prev = state.campaigns.length ? state.campaigns : [];

		const campaigns = (payload || []).map((campaign) => {
			const exists = prev.find((c) => c.id === campaign.id);
			if (exists) {
				// Prevent overwriting rulesets, which we may have already fetched.
				if (campaign.rulesets && !campaign.rulesets.length) {
					delete campaign.rulesets;
				}
				// Now merge the new campaign with the existing one.
				campaign = Object.assign({}, exists, campaign);
			}

			const sites = campaign.sites || [];
			fixDomains(sites);
			campaign.sites = sites;

			return campaign;
		});

		this._vm.$set(state, 'campaigns', campaigns);

		// Capture all known types and statuses on the first unfiltered load
		// so filter options remain stable when filters narrow the result set.
		if (!state.knownCampaignTypes.length) {
			const types = new Set();
			const statuses = new Set();
			let hasGamified = false;
			let hasMobile = false;
			campaigns.forEach((c) => {
				if (c.type) {
					types.add(c.type);
				}
				if (c.status && 'archive' !== c.status && 'trash' !== c.status) {
					statuses.add(c.status);
				}
				if (Number(c.is_gamified)) {
					hasGamified = true;
				}
				if (Number(c.is_mobile)) {
					hasMobile = true;
				}
			});
			if (hasGamified) {
				types.add('gamified');
			}
			if (hasMobile) {
				types.add('mobile');
			}
			state.knownCampaignTypes = Array.from(types);
			state.knownStatuses = Array.from(statuses);
		}
	},
	setTotalCampaignCount(state, count) {
		state.totalCampaignsCount = count;
	},
	setTotalFilteredCampaignCount(state, count) {
		state.totalFilteredCampaignsCount = count;
	},
	setCampaignStatus(state, { campaignId, splitId, status }) {
		const index = state.campaigns.findIndex((c) => c.id === campaignId);
		if (-1 === index) {
			return;
		}

		if (!splitId) {
			return this._vm.$set(state.campaigns[index], 'status', status);
		}

		const splitIndex = state.campaigns[index].splits.findIndex((s) => s.id === splitId);
		if (-1 !== splitIndex) {
			this._vm.$set(state.campaigns[index].splits[splitIndex], 'status', status);
		}
	},
	setStatus(state, { campaigns, status }) {
		campaigns.forEach((campaign) => {
			const index = state.campaigns.findIndex((c) => c.id === campaign);
			if (-1 !== index) {
				state.campaigns[index].status = status;
				if (state.campaigns[index].splits.length && 'paused' === status) {
					state.campaigns[index].splits.forEach((split, splitIndex) => {
						this._vm.$set(state.campaigns[index].splits[splitIndex], 'status', status);
					});
				}
			} else {
				// This is a split test, so we need to update it there.
				// Let's check if this is a split test.
				const campaignsWithSplits = state.campaigns.filter((c) => c.splits.length);
				campaignsWithSplits.forEach((c) => {
					const index = state.campaigns.findIndex((ca) => ca.id === c.id);
					const splitIndex = c.splits.findIndex((split) => split.id === campaign);
					if (-1 !== splitIndex) {
						this._vm.$set(state.campaigns[index].splits[splitIndex], 'status', status);
					}
				});
			}
		});
	},
	updateSort(state, payload) {
		let { campaigns, sort } = state;

		if (payload) {
			sort = payload.value;
			// Selecting a Newest/Oldest dropdown value discards any active column sort stack.
			this._vm.$set(state, 'sortStack', []);
		}

		// When column multi-sort is active and this is a post-fetch re-sort (no payload),
		// skip the in-memory sort — the API response is already sorted correctly.
		if (!payload && state.sortStack.length) {
			this._vm.$set(state, 'sort', sort);
			return;
		}

		// With an active stack, use the primary (first) column sort for in-memory ordering.
		const effectiveSort = state.sortStack.length ? state.sortStack[0].value : sort;
		const sortedCampaigns = sortUtil(campaigns, effectiveSort);

		this._vm.$set(state, 'campaigns', sortedCampaigns);
		this._vm.$set(state, 'sort', sort);
	},
	/**
	 * Add a column to the sort stack, or update its direction if already present.
	 * Position in the stack is preserved on direction change (priority unchanged).
	 *
	 * @param {object} state Vuex state.
	 * @param {object} payload Payload object.
	 * @param {string} payload.column Column key (e.g. 'visitors').
	 * @param {string} payload.dir Direction: 'asc' or 'desc'.
	 * @param {string} payload.value Sort value key (e.g. 'visitors-desc').
	 *
	 * @returns {void}
	 */
	upsertColumnSort(state, { column, dir, value }) {
		const index = state.sortStack.findIndex((s) => s.column === column);
		if (-1 === index) {
			state.sortStack.push({ column, dir, value });
		} else {
			this._vm.$set(state.sortStack, index, { column, dir, value });
		}
	},
	/**
	 * Remove a column from the sort stack (third-click / clear).
	 *
	 * @param {object} state Vuex state.
	 * @param {string} column Column key to remove.
	 *
	 * @returns {void}
	 */
	removeColumnSort(state, column) {
		const index = state.sortStack.findIndex((s) => s.column === column);
		if (-1 !== index) {
			this._vm.$delete(state.sortStack, index);
		}
	},
	refreshAnalytics(state, payload) {
		Object.keys(payload).forEach((version) => {
			Object.keys(payload[version]).forEach((campaignId) => {
				const campaign = payload[version][campaignId];
				const index = state.campaigns.findIndex((c) => c.id === campaignId);
				if (-1 !== index) {
					this._vm.$set(state.campaigns[index], 'analytics_data', {
						impressions: parseInt(campaign.impressions),
						conversions: parseInt(campaign.conversions),
						rate: parseFloat(campaign.rate.replace('%', '')),
					});
				} else {
					// Let's check if this is a split test.
					const campaignsWithSplits = state.campaigns.filter((c) => c.splits.length);
					campaignsWithSplits.forEach((c) => {
						const index = state.campaigns.findIndex((ca) => ca.id === c.id);
						const splitIndex = c.splits.findIndex((split) => split.id === campaignId);
						if (-1 !== splitIndex) {
							this._vm.$set(state.campaigns[index].splits[splitIndex], 'analytics_data', {
								impressions: parseInt(campaign.impressions),
								conversions: parseInt(campaign.conversions),
								rate: parseFloat(campaign.rate.replace('%', '')),
							});
						}
					});
				}
			});
		});
	},
	makePrimary(state, campaign) {
		const { campaigns } = state;
		const campaignIndex = campaigns.findIndex((c) => c.id === campaign.parent_id);
		const currentPrimary = { ...campaigns[campaignIndex] };
		const splits = currentPrimary.splits.filter((split) => split.id !== campaign.id);
		const newPrimary = { ...campaign };

		// Reset some settings for the new Primary.
		newPrimary.is_split = 0;
		newPrimary.sites = currentPrimary.sites.splice(0);
		newPrimary.folders = currentPrimary.folders.splice(0);
		newPrimary.splits = splits;
		delete newPrimary.parent_id;

		// Reset settings on the current primary.
		delete currentPrimary.sites;
		delete currentPrimary.folders;
		delete currentPrimary.splits;
		currentPrimary.parent_id = newPrimary.id;
		currentPrimary.is_split = 1;

		// Add the current primary as a split.
		newPrimary.splits.push(currentPrimary);

		this._vm.$set(state.campaigns, campaignIndex, newPrimary);
	},
	addToBulk(state, campaignId) {
		this._vm.$set(state.bulk, state.bulk.length, campaignId);

		if (state.bulk.length === state.campaigns.length) {
			this._vm.$set(state, 'isSelectAll', true);
		}
	},
	removeFromBulk(state, campaignId) {
		const index = state.bulk.indexOf(campaignId);
		if (-1 !== index) {
			this._vm.$delete(state.bulk, index);
			this._vm.$set(state, 'isSelectAll', false);
		}
	},
	removeCampaignByIndex(state, index) {
		this._vm.$delete(state.campaigns, index);
	},
	removeSplitByIndex(state, { campaignIndex, splitIndex }) {
		this._vm.$delete(state.campaigns[campaignIndex].splits, splitIndex);
	},
	clearBulk(state) {
		this._vm.$set(state, 'bulk', []);
		this._vm.$set(state, 'isSelectAll', false);
	},
	setBulk(state, campaigns) {
		this._vm.$set(state, 'bulk', campaigns);
	},
	selectAll(state, isOn) {
		this._vm.$set(state, 'isSelectAll', Boolean(isOn));
	},
	page(state, payload) {
		const page = parseInt(payload, 10);
		if (!Number.isNaN(page)) {
			state.page = page;
		}
	},
	publishCountUpdate(state, payload) {
		const count = parseInt(payload, 10);
		if (!Number.isNaN(count)) {
			state.publishCount = count;
		}
	},
	draftCountUpdate(state, payload) {
		const count = parseInt(payload, 10);
		if (!Number.isNaN(count)) {
			state.draftCount = count;
		}
	},
	setEmbedFilter(state, payload) {
		state.embedFilter = payload;

		if ('all' === payload) {
			this._vm.$set(state.filters, 'campaignIds', filterDefaults.campaignIds);
		}
	},
	setFilterValues(state, filters) {
		Object.keys(filters).forEach((key) => {
			this._vm.$set(state.filters, key, filters[key]);
		});
	},
	resetFilters(state) {
		Object.keys(filterDefaults).forEach((f) => {
			this._vm.$set(state.filters, f, filterDefaults[f]);
		});
		this._vm.$set(state, 'search', '');
		this._vm.$set(state, 'embedFilter', 'all');
	},
	resetFilter(state, key) {
		if ('search' === key) {
			return this._vm.$set(state, 'search', '');
		}
		if ('embedFilter' === key) {
			this._vm.$set(state, 'embedFilter', 'all');
			return this._vm.$set(state.filters, 'campaignIds', filterDefaults.campaignIds);
		}

		this._vm.$set(state.filters, key, filterDefaults[key]);
	},
	setError(state, payload) {
		state.error = payload;
	},
};
